import { selectUserOpsByAddress, userOpsStore } from '../store/userOpsStore';
import { isPaymasteredOp } from '../utils/isPaymasteredOp';
import { promptUser } from '../store/promptUser';
import { fallbackRequest } from './fallbackUseropRequest';
import { isCodeException } from '../errors';
import { decodeFunctionData, erc20Abi } from 'viem';
export async function prepareAndPrompt(args) {
    const { smartAccount, skipConfirmation, callData, enableUsdc } = args;
    const account = smartAccount.client.account;
    if (!account) {
        throw new Error('Smart account client account is undefined');
    }
    let preparedUserOp;
    const sender = smartAccount.address;
    try {
        preparedUserOp = (await smartAccount.prepareUserOperation({
            account,
            callData,
            hooks: {
                prePaymaster: async ({ userOp }) => {
                    // if we're not using usdc, attempt sponsorship
                    if (!enableUsdc || skipConfirmation) {
                        return { cancel: false };
                    }
                    const calls = await smartAccount.client.account?.decodeCalls?.(userOp.callData);
                    const usdcValue = await decodeUSDCValueFromCalldata(calls ?? []);
                    const isFreeTx = !usdcValue;
                    // free tx - continue with sponsorship
                    if (isFreeTx) {
                        return { cancel: false };
                    }
                    // continue showing prompt for non-free txs - user should approve the total cost of the tx (usdc gas + tx amount)
                    const tokenQuote = await smartAccount.quoteGasWithUSDC({
                        rpcRequest: userOp,
                    });
                    userOpsStore.getState().setCurrent({
                        sender,
                        op: fallbackRequest(smartAccount.client, userOp),
                        tokenQuote,
                    });
                    try {
                        await promptUser(sender);
                        return { cancel: false };
                    }
                    catch (error) {
                        return { cancel: true };
                    }
                },
                postTokenQuote: async ({ userOp, tokenQuote }) => {
                    if (skipConfirmation) {
                        return { cancel: false };
                    }
                    const calls = await smartAccount.client.account?.decodeCalls?.(userOp.callData);
                    const usdcValue = await decodeUSDCValueFromCalldata(calls ?? []);
                    const isFreeTx = !usdcValue;
                    // for non-free txs, we don't need to show the prompt again, it was triggered by the prePaymaster hook
                    if (!isFreeTx) {
                        return { cancel: false };
                    }
                    // continue showing prompt for free txs - user should approve the usdc gas costs, since they're paying for it
                    userOpsStore.getState().setCurrent({
                        sender,
                        op: fallbackRequest(smartAccount.client, userOp),
                        tokenQuote,
                    });
                    try {
                        await promptUser(sender);
                        return { cancel: false };
                    }
                    catch (error) {
                        return { cancel: true };
                    }
                },
            },
        }));
        userOpsStore.getState().setCurrent({
            sender,
            op: fallbackRequest(smartAccount.client, preparedUserOp),
        });
        const isPaymastered = isPaymasteredOp({
            entryPointVersion: account.entryPoint.version,
            paymasterAndData: preparedUserOp.paymasterAndData,
            paymasterData: preparedUserOp.paymasterData,
        });
        if (!isPaymastered) {
            // with usdc enabled, every single op should be paymastered - either sponsored, or have using pay gas with usdc paymaster data
            // if it's not, the user will have to have native ETH for gas, and we're not requiring that for the user for now, so instead just throw an error
            if (enableUsdc) {
                // if there's something actually wrong with the calldata, this would throw and provide a clearer error message
                // useful for debuggin joins on towns that are still ETH, but user is trying to join when we pass usdc as currency
                await smartAccount.quoteGasWithUSDC({
                    rpcRequest: preparedUserOp,
                    skipQuoteApi: true,
                });
                // otherwise, throw an error that the op is not paymastered
                throw new Error('There was an error preparing paymaster data for the user operation');
            }
            if (!skipConfirmation) {
                await promptUser(preparedUserOp.sender);
            }
        }
    }
    catch (error) {
        const { spaceId } = selectUserOpsByAddress(smartAccount.address).current;
        throw handlePrepareUserOperationError({
            error,
            smartAccount,
            spaceId,
        });
    }
    return preparedUserOp;
}
function handlePrepareUserOperationError(args) {
    const { error, smartAccount, spaceId } = args;
    if (isCodeException(error)) {
        if (error.category === 'userop_estimate_gas_failed') {
            console.error('[sendUserOperation::estimateGasLimit] calling estimateUserOperationGas failed:', {
                error,
            });
            // better logs
            const parsedError = smartAccount.parseAllContractErrors({
                spaceId,
                error,
            });
            if (parsedError) {
                return parsedError;
            }
        }
        else if (error.category === 'userop_pm_error_code') {
            console.error('[sendUserOperation::paymaster] paymaster rejected sponsorship:', {
                error,
            });
            userOpsStore
                .getState()
                .setRejectedSponsorshipReason(smartAccount.address, error.code);
        }
        else {
            console.error('[sendUserOperation] Failed to prepare user operation:', error);
        }
    }
    else {
        console.error('[sendUserOperation] Failed to prepare user operation:', error);
    }
    return error;
}
async function decodeUSDCValueFromCalldata(calls) {
    const data = calls.map((call) => call.data).filter((call) => call !== undefined);
    // at this point - these calls should not include gas approvals for usdc
    // they should include any approval calls for usdc transfer for things like tipping, joining space, etc.
    // that would indicate the value of the call
    // Find any ERC20 approve call and return the amount
    for (const callData of data) {
        try {
            const decoded = decodeFunctionData({ abi: erc20Abi, data: callData });
            if (decoded?.functionName === 'approve') {
                return decoded?.args[1]; // Return the amount being approved
            }
        }
        catch {
            // Not an ERC20 function, skip it
        }
    }
    return undefined;
}
